import { toast } from 'react-toastify'
import { useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import {
  InputWithValidationError,
  ResizibleTextArea,
  CheckboxWithDescription,
  SimpleCustomSelect
} from 'components/FormInputs'
import { FormSection } from 'components/Form'
import { PageHeader } from 'components/PageHeader'
import { LoadingCircle } from 'components/Loading'
import { serviceProviders, deviceTypes } from 'components/FormInputs/options'

import { ProductType } from 'models'
import { show, update } from '../services/api'
import { translations } from '../translations'

export function Edit() {
  const { productId } = useParams() as unknown as { productId: string }
  const navigate = useNavigate()

  const [isLoading, setIsLoading] = useState(true)
  const [product, setProduct] = useState<Record<string, any>>()
  const [subtype, setSubtype] = useState<string>('')
  const [nameError, setNameError] = useState<string | null>(null)

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        if (!productId) return
        setIsLoading(true)
        const data = await show(productId)
        setProduct(data)

        const existingSubtype = data.meta?.allowed_subtypes?.[0]
        if (existingSubtype) {
          setSubtype(existingSubtype)
        } else if (data.type === ProductType.connectivity) {
          setSubtype(serviceProviders[0].value)
        } else if (data.type === ProductType.hardware) {
          setSubtype(deviceTypes[0].value)
        }

        setIsLoading(false)
      } catch (err) {
        toast.error('Falha ao carregar produto')
        console.error(err)
      }
    }
    fetchProduct()
  }, [productId])

  const onAttributeChange = useCallback((attr: string, value: any) => {
    setProduct((prev) => ({ ...prev, [attr]: value }))
  }, [])

  const showSubtype = product?.type !== ProductType.software
  const subtypeOptions =
    product?.type === ProductType.connectivity ? serviceProviders : deviceTypes

  const onSave = useCallback(async () => {
    if (!product?.name?.trim()) {
      setNameError('O campo deve ser preenchido')
      return
    }
    setNameError(null)

    const meta =
      showSubtype && subtype ? { allowed_subtypes: [subtype] } : undefined

    try {
      await update(productId, {
        name: product.name,
        description: product.description ?? null,
        active: product.active,
        meta
      })
      toast.success('Produto atualizado com sucesso')
      navigate('/products')
    } catch (err: any) {
      toast.error(
        err.suggestedMessage ?? 'Houve uma falha ao tentar atualizar o produto'
      )
    }
  }, [productId, product, subtype, showSubtype, navigate])

  if (isLoading) return <LoadingCircle />

  return (
    <>
      <PageHeader title="Produtos" action="Editar" />
      <div className="flex flex-row space-x-8">
        <div className="w-3/5 px-4 animate-fade-in-down">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              onSave()
            }}
          >
            <FormSection title="Editar produto">
              <div className="py-4">
                <span className="text-sm font-medium text-gray-500">
                  Tipo:{' '}
                </span>
                <span className="text-sm text-gray-900">
                  {translations.type[product?.type as string] ??
                    product?.type ??
                    '-'}
                </span>
                <p className="text-xs text-gray-400 mt-1">
                  O tipo do produto não pode ser alterado após a criação.
                </p>
              </div>

              <InputWithValidationError
                label="Nome"
                name="name"
                value={product?.name ?? ''}
                onChange={(name) => onAttributeChange('name', name)}
                error={nameError}
              />

              <ResizibleTextArea
                label="Descrição"
                name="description"
                value={product?.description ?? ''}
                onChange={(description) =>
                  onAttributeChange('description', description)
                }
              />

              <fieldset className="space-y-5">
                <legend className="text-left">Estado de disponibilidade</legend>
                <CheckboxWithDescription
                  label="Ativado"
                  name="active"
                  checked={product?.active ?? false}
                  onChange={(active) => onAttributeChange('active', active)}
                  description="Ao ativar o produto, ele estará disponível para vincular a planos."
                />
              </fieldset>

              {showSubtype && (
                <SimpleCustomSelect
                  label="Restrição"
                  options={subtypeOptions}
                  value={subtype}
                  onChange={(value) => setSubtype(value)}
                />
              )}
            </FormSection>

            <div className="mt-12 flex justify-end">
              <button
                type="button"
                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
                onClick={() => navigate(-1)}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none"
              >
                Salvar
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}
